/**
 * harness/viewCounts.js
 * T-Cron-2026-10-24 14:00 (round-2-of-day) — 改进清单 #9「统计:view count」
 *
 * 缺口诊断(实测,不是猜测):
 *   改进清单 #9 有两个子项:
 *     ① 「今日新增指示」 —— ✅ T98(2026-10-23)已上线(Header 的「最新 xx-xx +N」胶囊)
 *     ② 「view count」   —— ❌ **完全没有任何实现**:
 *        `grep -rn "viewCount\|浏览量" vite/src/` → 0 命中。
 *
 * 为什么这个缺口值得填:
 *   看板是「选材」工具,不是「已发布内容」的数据看板 —— 浏览量本该是
 *   「发布后回来复盘哪个选题更受欢迎」的闭环信号。但现在用户根本没法知道
 *   自己**看过哪些**、**哪篇看了不止一次**,复盘只能凭记忆。
 *
 * 实现边界(刻意收窄,避免做成第二套后端):
 *   · **纯本地**:localStorage,不上报任何服务器。内容看板的数据是公开快照,
 *     浏览量是**使用者的个人行为**,没有跨设备同步的必要,也不该有隐私代价。
 *   · **只记录"打开详情"这一个动作**:滚动、筛选、切视图都不算。
 *     「看过」= 「点开读过正文」,这是唯一有意义的口径。
 *
 * 计数语义(这一步最容易做错,所以写清楚):
 *   计数 = 「**这一次阅读会话**被记了一次」,而不是「打开详情这个动作被触发了几次」。
 *   两种实现方式差别很大:
 *     · 事件计数:每次 openDetail 就 +1 ⇒ 用户手抖连点两下、或者 React
 *       严格模式把 effect 跑两遍,计数就虚高了 —— "已读 7 次"但其实只读过一遍。
 *     · 会话计数:同一次连续的阅读只记一次,真正离开(关掉详情)再回来才算新的一次。
 *   ⇒ 采用**时间窗去重**:同一篇在 DEDUP_WINDOW_MS 内重复打开只记一次。
 *     这个窗口(30s)同时覆盖了「连点」「严格模式双调用」「关掉又立刻重开」
 *     三种情况,而「隔一会儿回来重读」会被正确地记成新的一次。
 *
 * 存储形状:
 *   localStorage['xhs_view_counts_v1'] = { "<postId>": 12, ... }
 *   · 只保留有浏览过的 id ⇒ 空对象 = 从没看过。
 *   · 解析失败(隐私模式 / 配额满 / JSON 损坏)一律降级为空,绝不让看板崩。
 *
 * 计数上限:
 *   每条只存数字,id 数量 = 看过多少篇就存多少条 ⇒ 最多 ~450 条,
 *   JSON 体积 < 30KB,远低于 localStorage 5MB 配额。
 */

const STORAGE_KEY = 'xhs_view_counts_v1';

/**
 * 去重窗口(毫秒)。
 * 30s 覆盖「连点 / 严格模式双调用 / 关掉又立刻重开」,同时不会把
 * 「隔几分钟回来重读」误吞掉。
 */
const DEDUP_WINDOW_MS = 30_000;

/**
 * 本次页面生命周期内,每篇最后一次被记录的时刻(epoch ms)。
 * 用模块级 Map(不是 React state):它的作用是**去重**,不需要触发重渲染。
 */
const lastRecordedAt = new Map();

/**
 * 从 localStorage 读全部浏览次数。
 * @returns {Object<string, number>}
 */
export function loadViewCounts() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return {};
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    // 只保留「key 是字符串、value 是有限正整数」的项,其余丢弃。
    // (JSON 损坏 / 旧版本格式 / 被别的代码写脏了)
    const clean = {};
    for (const [k, v] of Object.entries(parsed)) {
      const n = Number(v);
      if (typeof k === 'string' && k && Number.isFinite(n) && n > 0) clean[k] = Math.floor(n);
    }
    return clean;
  } catch (_) {
    return {};
  }
}

/**
 * 写回 localStorage。
 * @param {Object<string, number>} counts
 */
function saveViewCounts(counts) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(counts || {}));
  } catch (_) {
    // 配额满 / 隐私模式 ⇒ 静默放弃。浏览量是锦上添花,绝不能因此把看板带崩。
  }
}

/**
 * 记录一次浏览。
 *
 * 去重规则(见文件头「计数语义」):
 *   同一篇在 DEDUP_WINDOW_MS 内重复打开 ⇒ 只记一次,返回现有计数。
 *   超出窗口 ⇒ 计数 +1。
 *
 * @param {string} postId
 * @returns {number} 记录后的累计次数(0 表示没记上)
 */
export function recordView(postId) {
  if (!postId) return 0;
  try {
    const now = Date.now();
    const last = lastRecordedAt.get(postId);
    if (last !== undefined && now - last < DEDUP_WINDOW_MS) {
      // 时间窗内 ⇒ 认为还是同一次阅读,不重复累加。
      return loadViewCounts()[postId] || 0;
    }
    const counts = loadViewCounts();
    const next = (counts[postId] || 0) + 1;
    counts[postId] = next;
    saveViewCounts(counts);
    lastRecordedAt.set(postId, now);
    return next;
  } catch (_) {
    return 0;
  }
}

/**
 * 取某个 post 的浏览次数(不记录)。
 * @param {string} postId
 * @returns {number}
 */
export function getViewCount(postId) {
  if (!postId) return 0;
  return loadViewCounts()[postId] || 0;
}

/**
 * 取「本次页面生命周期内打开过多少篇」——用于快速诊断。
 * 注意:与「已读过的总篇数」(localStorage 里的 id 数)口径不同,
 *       后者跨刷新保留,前者只在本次会话内。
 * @param {Array<string>} postIds 当前可见集合的 id
 * @returns {number}
 */
export function countSeenInSession(postIds) {
  if (!postIds || !postIds.length) return 0;
  let n = 0;
  for (const id of postIds) if (lastRecordedAt.has(id)) n += 1;
  return n;
}

/**
 * 清空全部浏览记录(用户主动清除)。
 * @returns {boolean} 是否真的清掉了
 */
export function clearViewCounts() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    window.localStorage.removeItem(STORAGE_KEY);
    lastRecordedAt.clear();
    return true;
  } catch (_) {
    return false;
  }
}

const ViewCounts = {
  loadViewCounts,
  recordView,
  getViewCount,
  countSeenInSession,
  clearViewCounts,
};

if (typeof window !== 'undefined') {
  window.ViewCounts = ViewCounts;
}

export default ViewCounts;
